import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AuthService } from '../../../../service/auth.service';

@Component({
  selector: 'app-new-password',
  templateUrl: './new-password.component.html',
  styleUrls: ['./new-password.component.scss']
})
export class NewPasswordComponent implements OnInit {
  newPasswordForm: FormGroup;
  loading = false;
  passwordVisible = false;
  confirmPasswordVisible = false;
  passwordStrength = {
    score: 0,
    feedback: '',
    color: ''
  };

  // Store credentials temporarily
  private username: string = '';
  private currentPassword: string = '';

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private messageService: MessageService
  ) {
    this.newPasswordForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(5)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });

    // Watch password changes for strength calculation
    this.newPasswordForm.get('password')?.valueChanges.subscribe(value => {
      this.calculatePasswordStrength(value);
    });
  }

  ngOnInit() {
    // Get stored credentials from navigation state or localStorage
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state) {
      this.username = navigation.extras.state['username'] || '';
      this.currentPassword = navigation.extras.state['password'] || '';
    }

    // Fallback to localStorage if navigation state not available
    if (!this.username || !this.currentPassword) {
      this.username = sessionStorage.getItem('temp_username') || '';
      this.currentPassword = sessionStorage.getItem('temp_password') || '';
    }

    if (!this.username || !this.currentPassword) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Session expired. Please login again.'
      });
      this.router.navigate(['/login']);
    }
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password');
    const confirmPassword = form.get('confirmPassword');
    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ mismatch: true });
      return { mismatch: true };
    }
    return null;
  }

  async onSubmit() {
    if (this.newPasswordForm.valid && this.username && this.currentPassword) {
      this.loading = true;
      const { password } = this.newPasswordForm.value;

      try {
        const response = await this.authService.changePassword(this.username, this.currentPassword, password);

        if (response && response.status === 'OK') {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Password changed successfully. Please login with your new password.'
          });

          // Clear temporary credentials
          sessionStorage.removeItem('temp_username');
          sessionStorage.removeItem('temp_password');

          // Navigate to login
          this.router.navigate(['/login']);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response?.message || 'Failed to change password'
          });
        }
      } catch (error: any) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error?.message || 'Failed to change password. Please try again.'
        });
      } finally {
        this.loading = false;
      }
    }
  }

  togglePasswordVisibility() {
    this.passwordVisible = !this.passwordVisible;
  }

  toggleConfirmPasswordVisibility() {
    this.confirmPasswordVisible = !this.confirmPasswordVisible;
  }

  calculatePasswordStrength(password: string) {
    if (!password) {
      this.passwordStrength = { score: 0, feedback: '', color: '' };
      return;
    }

    let score = 0;

    // Length check
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;

    // Character variety checks
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    // Determine strength
    if (score < 3) {
      this.passwordStrength = {
        score,
        feedback: 'Weak',
        color: '#ef4444'
      };
    } else if (score < 5) {
      this.passwordStrength = {
        score,
        feedback: 'Medium',
        color: '#f59e0b'
      };
    } else {
      this.passwordStrength = {
        score,
        feedback: 'Strong',
        color: '#10b981'
      };
    }
  }

  goToLogin() {
    this.router.navigate(['/login']);
  }
}
